import React from 'react';
import { Link } from 'react-router-dom';
import { BriefcaseIcon, DropletsIcon, InfinityIcon, GiftIcon, GlassesIcon, ZapIcon } from 'lucide-react';
import { includedGear, GearItem } from '../data/product';
import { useTrackOnView } from '../hooks/useTrackOnView';
const icons = [ZapIcon, GlassesIcon, BriefcaseIcon, DropletsIcon, InfinityIcon, GiftIcon];

/**
 * The one visual value stack: what physically shows up in the box. Product
 * renders drop into `includedGear[].image`; until the branded hard case and
 * anti-fog renders exist, those tiles show a neutral GritVue placeholder
 * rather than a generic stand-in product that is not ours.
 */
function GearVisual({
  item,
  Icon
}: {
  item: GearItem;
  Icon: React.ComponentType<{
    className?: string;
  }>;
}) {
  if (item.image) {
    return <div className="flex aspect-[4/3] w-full items-center justify-center border border-white/10 bg-ink-900" data-id="element-291" data-mp="hvefhpm7nhjg">
        <img src={item.image} alt={item.name} className="max-h-full w-full object-contain" loading="lazy" data-id="element-292" data-mp="mmwggdo8hi4f" />
      </div>;
  }
  const isMystery = /mystery/i.test(item.name);
  return <div className={`flex aspect-[4/3] w-full items-center justify-center border ${isMystery ? 'border-grit-500/40 bg-ink-900' : 'border-white/10 bg-ink-900'}`} aria-hidden="true" data-id="element-293" data-mp="1lb1fxzgzswey">
      {isMystery ? <span className="font-display text-6xl font-extrabold leading-none text-grit-500" data-id="element-294" data-mp="1uaehm5yvkgp4">?</span> : <Icon className="h-12 w-12 text-steel-500" data-id="element-295" data-mp="28y98dgtboazk" />}
    </div>;
}
export function IncludedGear() {
  const ref = useTrackOnView<HTMLElement>('mystery_gift_offer_viewed');
  return <section ref={ref} aria-labelledby="included-gear-heading" className="mt-10 border border-white/12 bg-ink-950 lg:mt-12" data-id="element-296" data-mp="2i08qydjzjn4m">
      <div className="border-b border-white/10 px-5 py-5 sm:px-7" data-id="element-297" data-mp="nmhk1lt1jhcv">
        <h3 id="included-gear-heading" className="grit-headline text-[clamp(1.5rem,4vw,2.5rem)] font-extrabold uppercase text-offwhite" data-id="element-298" data-mp="nou0fuj5hjnt">
          Look at everything in the box.
        </h3>
        <p className="mt-2 text-[15px] text-steel-400" data-id="element-299" data-mp="2noz997zkn1a6">
          The case and the anti-fog are not upsells. They ship with the order.
        </p>
      </div>

      <ul className="grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3" data-id="element-300" data-mp="2smin46scxc1l">
        {includedGear.map((item, index) => {
        const Icon = icons[index] ?? GiftIcon;
        const isWarranty = /warranty/i.test(item.name);
        const body = <>
              <GearVisual item={item} Icon={Icon} data-id="element-301" data-mp="36n38napnplys" />
              <span className="mt-4 flex items-start justify-between gap-3" data-id="element-302" data-mp="36liazc81pmwp">
                <span className="font-display text-xl font-bold uppercase leading-tight tracking-wide text-offwhite" data-id="element-303" data-mp="3bfd3n55mpc7x">
                  {item.name}
                </span>
                <span className={`shrink-0 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${isWarranty ? 'gold-plate text-ink-950' : 'bg-grit-500 text-ink-950'}`} data-id="element-304" data-mp="3kgayawu6g7du">
                  {item.tag}
                </span>
              </span>
              <span className="mt-1.5 block text-[14px] leading-relaxed text-steel-300" data-id="element-305" data-mp="18qcx5z29i1v9">{item.note}</span>
              <span className="mt-auto block pt-3 text-[11px] font-bold uppercase tracking-[0.16em] text-steel-500" data-id="element-306" data-mp="14qkt9zntcjw8">
                {item.qty}
              </span>
            </>;
        return <li key={item.name} className="bg-ink-950" data-id="element-307" data-mp="1wjl7bage19g3" data-mpi={index}>
              {isWarranty ? <Link to="/legal/guarantee" className="flex h-full flex-col p-5 transition-colors duration-150 ease-grit hover:bg-white/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f2c94c] sm:p-6" data-id="element-308" data-mp="1sgovq3p9mdln">
                  {body}
                </Link> : <div className="flex h-full flex-col p-5 sm:p-6" data-id="element-309" data-mp="2bf14vpg6fnh8">{body}</div>}
            </li>;
      })}
      </ul>
    </section>;
}